// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductImageStyles } from "@hardwoods/Overrides/Components/ProductImage";
import { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import { SmallHeadingAndTextStyles } from "@hardwoods/Overrides/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@hardwoods/Overrides/Widgets/Cart/CartLineNotes";
import { CartLineQuantityStyles } from "@hardwoods/Overrides/Widgets/Cart/CartLineQuantity";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import { partNumberStyle } from "@hardwoods/Overrides/Widgets/ProductList/helper";
import List from "@hardwoods/XDL/Icons/List";
import { ellipsis } from "@hardwoods/XDL/Utilities";
import { iconLinkCommonStyles } from "@hardwoods/XDL/Utilities/iconButtonCommonStyles";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { hideElement } from "@hardwoods/XDL/Utilities/SharedStyles";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const flexBasisForThreeRightItems = {
    infoAndImageContainer: `58%`,
    qtyAndPriceContainer: `42%`,
    qtyAndPriceItems: `${(1 / 3) * 100}%`,
};

export const flexBasisForFourRightItems = {
    infoAndImageContainer: `50%`,
    qtyAndPriceContainer: `50%`,
    qtyAndPriceItems: `25%`,
};

export interface CartLineCardStyles {
    container?: InjectableCss;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    orderLineInfoContainer?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: InjectableCss;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    qtyAndPriceContainer?: InjectableCss;
    priceContainer?: InjectableCss;
    totalPriceContainer?: InjectableCss;
    infoAndImageContainer?: InjectableCss;
    actionsGridItem?: GridItemProps;
    hidePartNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    infoSection?: InjectableCss;
    qtyContainer?: InjectableCss;
    notesAndAddToListContainer?: InjectableCss;
    removeContainer?: InjectableCss;
    partNumbersHeadingAndTextStyles: SmallHeadingAndTextStyles;
    productInfoSectionOptionsText?: TypographyProps;
    noteText?: TypographyProps;
    subtotalLabel?: TypographyProps;
    minPackQuantityGridItem?: GridItemProps;
    minPackQuantityLabelText?: TypographyPresentationProps;
    minPackQuantityValueText?: TypographyPresentationProps;
}

const cartLineCardStyles: CartLineCardStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 20px 0;
            width: 100%;
            @media print {
                break-inside: avoid;
            }
        `,
    },
    infoSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                flex-direction: column;
                flex-wrap: nowrap;
            }
            @media print {
                break-inside: avoid;
                display: block;
            }
        `,
    },
    actionsGridItem: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                flex-direction: column;
                flex-wrap: nowrap;
            }
        `,
    },
    removeContainer: {
        css: css`
            flex-basis: 15%;
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                flex-basis: auto;
            }
        `,
    },
    notesAndAddToListContainer: {
        css: css`
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            padding-left: 147px;
            padding-right: 20px;

            flex: 0 1 70%;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                padding-left: 0;
                justify-content: flex-start;
                flex-wrap: wrap;
            }
            ${xScreen.lte(cartLineBreakpoints.sm)} {
                flex-direction: column;
                margin-bottom: 10px;
            }
        `,
    },
    cartLineNotesGridItem: {
        css: css`
            margin-right: 50px;
        `,
    },
    infoAndImageContainer: {
        css: css`
            flex: 1 0 ${flexBasisForThreeRightItems.infoAndImageContainer};
            display: flex;
            padding-right: 20px;
            align-items: flex-start;
            margin-bottom: 10px;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                margin-bottom: 20px;
            }
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                flex-direction: column;
            }
        `,
    },
    qtyAndPriceContainer: {
        css: css`
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: space-between;
            display: flex;
            > div {
                flex: 1 1 ${flexBasisForThreeRightItems.qtyAndPriceItems};
            }
            ${xScreen.lte(cartLineBreakpoints.md)} {
                flex-wrap: wrap;
                flex-basis: auto;
                > div {
                    min-width: 150px;
                    margin-bottom: 12px;
                    padding-right: 20px;
                    flex: 1 1 auto;
                }
            }
        `,
    },
    qtyContainer: {
        css: css`
            padding-right: 15px;
            display: flex;
            align-items: flex-start;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                order: 1;
            }
        `,
    },
    totalPriceContainer: {
        css: css`
            word-break: break-word;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                order: 3;
                margin-top: 0;
                margin-bottom: auto;
                text-align: center;
            }
            ${xScreen.lte(cartLineBreakpoints.sm)} {
                text-align: left;
            }

            // to add small padding when qty editable to make design better
            &.cartLine-editable {
                padding-top: 9px;
                ${xScreen.lte(cartLineBreakpoints.md)} {
                    padding-top: 0;
                }
            }
        `,
    },
    priceContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            > ${TypographyStyle}, > div {
                margin-bottom: 8px;
            }

            ${xScreen.lte(cartLineBreakpoints.md)} {
                flex-direction: row;
                flex-wrap: nowrap;
                justify-content: space-around;
                order: 2;
                > section,
                > div {
                    width: auto;
                    margin-right: 20px;
                    margin-bottom: 8px;
                }
            }
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                flex-wrap: wrap;
                justify-content: flex-start;
            }

            // to add small padding when qty editable to make design better
            &.cartLine-editable {
                padding-top: 9px;
                & .productPriceWrapper.priceUOM {
                    margin-top: -9px;
                }
                ${xScreen.lte(cartLineBreakpoints.md)} {
                    padding-top: 0;
                    & .productPriceWrapper.priceUOM {
                        margin-top: 0;
                    }
                }
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                margin-right: 20px;
                ${xScreen.lte(cartLineBreakpoints.xs)} {
                    margin-right: auto;
                    margin-left: auto;
                    margin-bottom: 8px;
                }
            `,
        },
        image: {
            width: "127px",
            height: "127px",
            css: css`
                max-width: 100%;
                max-height: 100%;
                border: 1px solid ${getColor("common.border")};
                border-radius: 4px;

                img {
                    max-width: 100%;
                    max-height: 100%;
                }
            `,
        },
    },
    cartLineInfoContainer: { gap: 5 },
    orderLineInfoContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100%;
            > div {
                width: 100%;
                margin-bottom: 5px;
                &:last-child {
                    margin-bottom: 0;
                }
            }
            @media print {
                display: block;
            }
        `,
    },
    productBrandAndDescriptionGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
            @media print {
                display: block;
            }
        `,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: { width: 12 },
    productPartNumbers: {
        erpNumberLabelText: partNumberStyle.label,
        erpNumberValueText: partNumberStyle.value,
        manufacturerItemLabelText: partNumberStyle.label,
        manufacturerItemValueText: partNumberStyle.value,
        customerNameLabelText: partNumberStyle.label,
        customerNameValueText: partNumberStyle.value,
    },
    promotionNameText: {
        ...partNumberStyle.value,
        weight: "normal",
    },
    productDescription: {
        productDetailLink: {
            css: css`
                width: 100%;
                margin-bottom: 5px;
            `,
            typographyProps: {
                size: 14,
                weight: "bold",
                css: css`
                    letter-spacing: 0;
                    line-height: 20px;
                    ${ellipsis(2)}
                `,
            },
        },
    },
    availabilityGridItem: {
        width: 12,
        css: css`
            &&& {
                margin-top: 10px;
            }
        `,
    },
    configurationOptionText: {
        ...partNumberStyle.value,
        weight: "normal",
    },
    productBrand: {
        nameLink: {
            css: css`
                margin-top: 15px;
            `,
        },
        logoImage: {
            width: "100px",
            height: "70px",
        },
    },
    quantity: {
        editableQuantityTextField: {
            labelProps: {
                css: css`
                    display: none;
                `,
            },
            cssOverrides: {
                formField: css`
                    width: auto;
                `,
            },
        },
        readOnlyQuantityText: {
            heading: {
                css: css`
                    display: none;
                    ${xScreen.lte(cartLineBreakpoints.md)} {
                        display: block;
                    }
                    @media print {
                        display: block;
                    }
                `,
            },
            text: {
                css: css`
                    padding-left: 10px;
                    ${xScreen.lte(cartLineBreakpoints.md)} {
                        padding-left: 0;
                    }
                `,
            },
        },
    },
    addToListLink: {
        ...iconLinkCommonStyles(List, {
            variantType: "secondary",
            sizeVariant: "small",
            css: css`
                flex-shrink: 0;
            `,
        }),
    },
    extendedUnitNetPriceGridItem: {
        css: css`
            flex-direction: column;
            align-items: flex-start;
            justify-content: flex-end;
            line-height: 22px;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                text-align: right;
            }
            ${xScreen.lte(cartLineBreakpoints.sm)} {
                text-align: left;
            }
        `,
    },
    productPrice: {
        priceLabelText: {
            css: css`
                display: none;
                padding-left: 0;
                ${xScreen.lte(cartLineBreakpoints.md)} {
                    display: block;
                }
                @media print {
                    display: block;
                }
            `,
        },
        netPrice: {
            priceText: { size: 16, weight: "bold" },
            unitOfMeasureText: { size: 12, weight: "normal" },
        },
    },
    extendedUnitNetPriceText: {
        weight: "bold",
        size: 20,
        css: css`
            letter-spacing: 0;
            line-height: 22px;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    subtotalWithoutVatText: {
        weight: "bold",
        css: css`
            margin-top: 5px;
        `,
    },
    cartLineErrorMessageGridItem: { width: 12 },
    deliveryScheduleButton: {
        css: css`
            margin-top: 20px;
        `,
    },
    costCodeGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    costCodeLabelText: {
        ...mergeWithCss(partNumberStyle.label, {
            css: css`
                margin-top: 10px;
                margin-bottom: 4px;
            `,
        }),
    },
    costCodeText: {
        ...partNumberStyle.value,
    },
    costCodeSelect: {
        cssOverrides: {
            formField: css`
                ${getProp("theme.formField.defaultProps.cssOverrides.inputSelect")}
                max-width: 200px;
            `,
        },
    },
    partNumbersHeadingAndTextStyles: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: row;
                > ${TypographyStyle} {
                    flex-shrink: 0;
                }
            `,
        },
        heading: { ...partNumberStyle.label, transform: "capitalize" },
        text: partNumberStyle.value,
    },
    productInfoSectionOptionsText: {
        ...partNumberStyle.value,
        weight: "normal",
    },
    noteText: {
        ...partNumberStyle.value,
        weight: "normal",
    },
    subtotalLabel: {
        variant: "legend",
        size: 10,
        transform: "uppercase",
        color: "text.accent",
        weight: "bold",
        css: css`
            display: none;
            line-height: 15px;
            padding-left: 0;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                display: block;
            }
        `,
    },
    minPackQuantityGridItem: {
        width: 12,
    },
    minPackQuantityLabelText: {
        css: css`
            font-size: 12px;
            margin-right: 5px;
            &::after {
                content: ":";
            }
        `,
    },
    minPackQuantityValueText: {
        weight: "bold",
        css: css`
            font-size: 12px;
        `,
    },
};

cartLineCardStyles.hidePartNumbersStyles = {
    ...cartLineCardStyles.productPartNumbers,
    erpNumberGridItem: {
        width: 0,
        css: css`
            ${hideElement}
        `,
    },
};

export default cartLineCardStyles;
